export type TipoDocumento =
  | 'CPF'
  | 'CNPJ'
  | 'INVALIDO';

/**
 * Normalização canônica de documento.
 *
 * - Retorna null para ausência de valor.
 * - Remove apenas a pontuação conhecida de CPF/CNPJ.
 * - Remove espaços.
 * - Converte letras para maiúsculas.
 * - NÃO remove caracteres proibidos.
 *
 * Dessa forma, caracteres inválidos continuam presentes
 * para que a validação consiga rejeitá-los.
 */
export function normalizeDocumento(
  value: string | null | undefined
): string | null {

  if (!value) {
    return null;
  }
 
  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/[.\/-]/g, '');

  return normalized || null;

}

/**
 * Adaptador mantido temporariamente por compatibilidade.
 *
 * Não remove caracteres proibidos silenciosamente,
 * pois isso poderia transformar uma entrada inválida
 * em válida.
 */
export function normalizeAlfaNumeric(
  value: string | null | undefined
): string {
  return normalizeDocumento(value) ?? '';
}


export function isCpfValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  if (!/^[0-9]{11}$/.test(normalized)) {
    return false;
  }

  if (/^(\d)\1+$/.test(normalized)) {
    return false;
  }

  const digits = normalized
    .split('')
    .map(digit => parseInt(digit, 10));

  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }

  let firstCheck = (sum * 10) % 11;

  if (firstCheck === 10) {
    firstCheck = 0;
  }

  if (firstCheck !== digits[9]) {
    return false;
  }

  sum = 0;

  for (let i = 0; i < 10; i++) {
    sum += digits[i] * (11 - i);
  }

  let secondCheck = (sum * 10) % 11;

  if (secondCheck === 10) {
    secondCheck = 0;
  }

  return secondCheck === digits[10];
}

function charToValue(char: string): number {
  if (char >= '0' && char <= '9') {
    return parseInt(char, 10);
  }

  if (char >= 'A' && char <= 'Z') {
    return char.charCodeAt(0) - 48;
  }

  return -1;
}

/**
 * Implementação única do módulo 11 para CNPJ.
 *
 * Recebe as 12 posições da base do CNPJ e retorna
 * os dois dígitos verificadores.
 */
function calcularDvCnpj(base: string): string | null {

  if (!/^[A-Z0-9]{12}$/.test(base)) {
    return null;
  }

  const values: number[] = [];

  for (const char of base) {
    const value = charToValue(char);

    if (value < 0) {
      return null;
    }

    values.push(value);
  }

  const weights1 = [
    5, 4, 3, 2,
    9, 8, 7, 6,
    5, 4, 3, 2
  ];

  const weights2 = [
    6, 5, 4, 3,
    2, 9, 8, 7,
    6, 5, 4, 3, 2
  ];

  let sum = 0;

  for (let i = 0; i < 12; i++) {
    sum += values[i] * weights1[i];
  }

  let firstCheck = sum % 11;
  firstCheck = firstCheck < 2 ? 0 : 11 - firstCheck;

  sum = 0;

  const valuesWithFirst = [
    ...values,
    firstCheck
  ];

  for (let i = 0; i < 13; i++) {
    sum += valuesWithFirst[i] * weights2[i];
  }

  let secondCheck = sum % 11;
  secondCheck = secondCheck < 2 ? 0 : 11 - secondCheck;

  return `${firstCheck}${secondCheck}`;
}

export function isCnpjValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  if (!/^[0-9]{14}$/.test(normalized)) {
    return false;
  }

  if (/^(\d)\1+$/.test(normalized)) {
    return false;
  }

  const base = normalized.substring(0, 12);
  const checkDigits = normalized.substring(12);

  return calcularDvCnpj(base) === checkDigits;
}

export function isCnpjAlfanumericoValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(normalized)) {
    return false;
  }

  if (/^[0-9]{14}$/.test(normalized)) {
    return isCnpjValid(normalized);
  }

  const base = normalized.substring(0, 12);
  const checkDigits = normalized.substring(12);

  return calcularDvCnpj(base) === checkDigits;
}

export function isCnpjCompletoValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(normalized)) {
    return false;
  }

  return isCnpjAlfanumericoValid(normalized);
}

export function identificarTipoDocumento(
  value: string | null | undefined
): TipoDocumento {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return 'INVALIDO';
  }

  if (/^[0-9]{11}$/.test(normalized)) {
    return 'CPF';
  }

  if (/^[A-Z0-9]{12}[0-9]{2}$/.test(normalized)) {
    return 'CNPJ';
  }

  return 'INVALIDO';
}

export function isCpfCnpjValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  const tipo = identificarTipoDocumento(normalized);

  if (tipo === 'CPF') {
    return isCpfValid(normalized);
  }

  if (tipo === 'CNPJ') {
    return isCnpjCompletoValid(normalized);
  }

  return false;
}

/**
 * Extrai as oito posições da raiz de um CNPJ completo.
 */
export function getCnpjRaiz(
  value: string | null | undefined
): string {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return '';
  }

  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(normalized)) {
    return '';
  }

  return normalized.substring(0, 8);
}

/**
 * Valida somente o contrato explícito de raiz.
 */
export function isCnpjRaizValid(
  value: string | null | undefined
): boolean {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return false;
  }

  return /^[A-Z0-9]{8}$/.test(normalized);
}

export function formatCpf(
  value: string | null | undefined
): string {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return '';
  }

  if (!/^[0-9]{11}$/.test(normalized)) {
    return value || '';
  }

  return normalized.replace(
    /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
    '$1.$2.$3-$4'
  );
}

export function formatCnpj(
  value: string | null | undefined
): string {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return '';
  }

  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(normalized)) {
    return value || '';
  }

  return normalized.replace(
    /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([0-9]{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

export function formatDocumento(
  value: string | null | undefined
): string | null {

  const normalized = normalizeDocumento(value);

  if (!normalized) {
    return null;
  }

  const tipo = identificarTipoDocumento(normalized);

  if (tipo === 'CPF') {
    return formatCpf(normalized);
  }

  if (tipo === 'CNPJ') {
    return formatCnpj(normalized);
  }

  return normalized;
}